/**
 * Admin Economic Reset Page
 * 
 * Epoch-based economic reset tool for deterministic testing.
 * 
 * SAFETY FEATURES:
 * - BLOCKED in production environments
 * - Two-step confirmation: Preview → Execute
 * - Requires exact confirmation phrase
 * - Shows dry-run preview before any changes
 * - Audit logging for all actions
 * 
 * WHAT GETS RESET:
 * - All credit ledger entries
 * - All financial transactions
 * - All subscription records
 * - All billing profiles
 * - User membership/subscription fields (NOT identity)
 * 
 * WHAT IS PROTECTED:
 * - User accounts (email, password, roles)
 * - Manuscripts (book catalog)
 * - Author profiles
 * - Pricing configurations
 */
import React, { useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { 
  AlertTriangle, RefreshCw, Shield, Trash2, 
  CheckCircle, XCircle, Eye, Lock, Loader2,
  FileWarning, Database, Users, CreditCard
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AdminNav from "../components/AdminNav";
import AdminTopMenuBar from "../components/AdminTopMenuBar";
import UniversalFooter from "../components/UniversalFooter";
import { useNotification } from '@/contexts/NotificationContext';
import { API_URL } from '@/config/api';

// API_URL imported from centralized config

const CONFIRMATION_PHRASE = "RESET_ECONOMIC_STATE";

const AdminResetPage = () => {
  const { user } = useAuth();
  
  // State
  const { notifyError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [executeResult, setExecuteResult] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  
  const userId = user?.user_id || user?.id;
  const userEmail = user?.email;

  // Fetch preview
  const fetchPreview = useCallback(async () => {
    if (!userId || !userEmail) {
      notifyError("User information not available");
      return;
    }
    
    setLoading(true);
    setPreview(null);
    setExecuteResult(null);
    setConfirmPhrase("");
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/system/reset-economic-state/preview?admin_email=${encodeURIComponent(userEmail)}&admin_id=${encodeURIComponent(userId)}`
      );
      
      if (response.status === 403) {
        const error = await response.json();
        notifyError("Reset Blocked", {
          description: error.detail || "This tool is blocked in production"
        });
        return;
      }
      
      if (!response.ok) {
        throw new Error("Failed to fetch preview");
      }
      
      const data = await response.json();
      setPreview(data);
      console.log('[SUCCESS] ' + "Preview generated", {
        description: `Nonce expires in 5 minutes`
      });
    } catch (error) {
      console.error("Preview error:", error);
      notifyError("Failed to generate preview", {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  }, [userId, userEmail]);

  // Execute reset
  const executeReset = useCallback(async (dryRun = false) => {
    if (!preview?.nonce) {
      notifyError("No valid preview. Generate a new preview first.");
      return;
    }
    
    if (confirmPhrase !== CONFIRMATION_PHRASE) {
      notifyError("Incorrect confirmation phrase", {
        description: `Must be exactly: ${CONFIRMATION_PHRASE}`
      });
      return;
    }
    
    setExecuting(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/system/reset-economic-state/execute`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nonce: preview.nonce,
            confirmation_phrase: confirmPhrase,
            dry_run: dryRun
          })
        }
      );
      
      if (response.status === 403) {
        notifyError("Execution Blocked", {
          description: "This tool is blocked in production"
        });
        return;
      }
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Execution failed");
      }
      
      const result = await response.json();
      setExecuteResult(result);
      setConfirmDialogOpen(false);
      
      if (dryRun) {
        console.log('[INFO] ' + "Dry Run Complete", {
          description: "No changes were made"
        });
      } else {
        console.log('[SUCCESS] ' + "Reset Complete", {
          description: `Epoch advanced to ${result.new_epoch}`
        });
        // Clear preview since nonce is consumed
        setPreview(null);
        setConfirmPhrase("");
      }
    } catch (error) {
      console.error("Execute error:", error);
      notifyError("Execution failed", {
        description: error.message
      });
    } finally {
      setExecuting(false);
    }
  }, [preview, confirmPhrase]);

  const phraseMatches = confirmPhrase === CONFIRMATION_PHRASE;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <AdminNav />
      <AdminTopMenuBar />
      
      <div className="pb-20 md:pb-0">
        {/* Header */}
        <header className="bg-gradient-to-br from-red-900/30 via-slate-800 to-slate-900 border-b border-red-800/50">
          <div className="mx-auto max-w-4xl px-4 py-10">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="w-8 h-8 text-red-400" />
              <h1 className="text-3xl font-bold text-white">
                Economic Reset Tool
              </h1>
              <Badge variant="outline" className="ml-2 text-red-400 border-red-400/50">
                DESTRUCTIVE
              </Badge>
            </div>
            <p className="text-slate-300">
              Epoch-based reset for deterministic testing. Clears all financial and credit data while preserving user identities and catalog.
            </p>
          </div>
        </header>

        <main className="mx-auto max-w-4xl px-4 py-8 space-y-6">
          
          {/* Warning Alert */}
          <Alert variant="destructive" className="bg-red-900/20 border-red-800">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className="text-red-300">Reader-Scoped Reset</AlertTitle>
            <AlertDescription className="text-red-200/80">
              This will permanently delete all <strong>Reader</strong> credit ledger entries, 
              financial transactions, subscription records, and billing profiles. 
              <br /><br />
              <strong>PROTECTED (Never Touched):</strong> User identities, manuscripts, author profiles, 
              publisher data, admin data, and all pricing/system configurations.
            </AlertDescription>
          </Alert>

          {/* Step 1: Generate Preview */}
          <Card className="bg-slate-900/60 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-amber-400" />
                Step 1: Generate Preview
              </CardTitle>
              <CardDescription className="text-slate-400">
                See exactly what will be affected before making any changes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={fetchPreview}
                disabled={loading}
                className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Generate Dry-Run Preview
              </Button>
            </CardContent>
          </Card>

          {/* Preview Results */}
          {preview && (
            <Card className="bg-slate-900/60 border-amber-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Database className="w-5 h-5 text-amber-400" />
                  Preview Results
                  <Badge className="ml-2 bg-amber-600/20 text-amber-300 border-amber-500/30">
                    Epoch {preview.current_epoch} → {preview.proposed_epoch}
                  </Badge>
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Environment: <span className="text-amber-400 font-mono">{preview.environment}</span>
                  {" • "}
                  Nonce expires: {new Date(preview.nonce_expires_at).toLocaleTimeString()}
                  {" • "}
                  <span className="text-green-400 font-bold">READER-SCOPED</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Collections to Clear */}
                <div>
                  <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                    <Trash2 className="w-4 h-4 text-red-400" />
                    Collections to Clear ({preview.total_documents_to_delete} documents)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(preview.collections_to_clear).map(([name, count]) => (
                      <div 
                        key={name} 
                        className={`px-3 py-2 rounded-lg text-sm ${
                          count > 0 
                            ? 'bg-red-900/30 text-red-300 border border-red-700/50' 
                            : 'bg-slate-800 text-slate-500'
                        }`}
                      >
                        <span className="font-mono">{name}</span>
                        <span className="ml-2 font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* User Resets */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-amber-900/20 rounded-lg p-4 border border-amber-700/30">
                    <div className="flex items-center gap-2 text-amber-300 mb-1">
                      <Users className="w-4 h-4" />
                      <span className="font-bold">Users to Reset</span>
                    </div>
                    <div className="text-2xl font-bold text-white">{preview.users_to_reset}</div>
                    <div className="text-xs text-amber-200/60">Economic fields only (membership, subscription)</div>
                  </div>
                  <div className="bg-amber-900/20 rounded-lg p-4 border border-amber-700/30">
                    <div className="flex items-center gap-2 text-amber-300 mb-1">
                      <CreditCard className="w-4 h-4" />
                      <span className="font-bold">Billing Profiles</span>
                    </div>
                    <div className="text-2xl font-bold text-white">{preview.billing_profiles_to_clear}</div>
                    <div className="text-xs text-amber-200/60">Will require card re-entry</div>
                  </div>
                </div>

                {/* Protected Collections */}
                <div>
                  <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-green-400" />
                    Protected Collections (NOT touched)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(preview.protected_collections).map(([name, count]) => (
                      <div 
                        key={name} 
                        className="px-3 py-2 rounded-lg text-sm bg-green-900/20 text-green-300 border border-green-700/30"
                      >
                        <span className="font-mono">{name}</span>
                        <span className="ml-2 font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Warnings */}
                {preview.warnings?.length > 0 && (
                  <Alert className="bg-yellow-900/20 border-yellow-700">
                    <FileWarning className="h-4 w-4 text-yellow-400" />
                    <AlertTitle className="text-yellow-300">Warnings</AlertTitle>
                    <AlertDescription className="text-yellow-200/80">
                      <ul className="list-disc list-inside">
                        {preview.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 2: Confirm and Execute */}
          {preview && (
            <Card className="bg-slate-900/60 border-red-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Lock className="w-5 h-5 text-red-400" />
                  Step 2: Confirm and Execute
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Type the confirmation phrase exactly to enable execution
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-slate-300 mb-2">
                    Type: <span className="font-mono text-red-400">{CONFIRMATION_PHRASE}</span>
                  </label>
                  <Input
                    type="text"
                    value={confirmPhrase}
                    onChange={(e) => setConfirmPhrase(e.target.value)}
                    placeholder="Type confirmation phrase..."
                    className={`bg-slate-800 border-slate-600 text-white font-mono ${
                      confirmPhrase && (phraseMatches ? 'border-green-500' : 'border-red-500')
                    }`}
                    data-testid="confirmation-phrase-input"
                  />
                  {confirmPhrase && (
                    <div className={`mt-1 text-sm flex items-center gap-1 ${
                      phraseMatches ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {phraseMatches ? (
                        <><CheckCircle className="w-4 h-4" /> Phrase matches</>
                      ) : (
                        <><XCircle className="w-4 h-4" /> Phrase does not match</>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => executeReset(true)}
                    disabled={!phraseMatches || executing}
                    className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                  >
                    {executing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Eye className="w-4 h-4 mr-2" />
                    )}
                    Dry Run (No Changes)
                  </Button>
                  
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmDialogOpen(true)}
                    disabled={!phraseMatches || executing}
                    className="bg-red-600 hover:bg-red-700 font-bold"
                    data-testid="execute-reset-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Execute Reset
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Execution Result */}
          {executeResult && (
            <Card className={`border ${
              executeResult.dry_run 
                ? 'bg-amber-900/20 border-amber-700' 
                : 'bg-green-900/20 border-green-700'
            }`}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${
                  executeResult.dry_run ? 'text-amber-300' : 'text-green-300'
                }`}>
                  {executeResult.dry_run ? (
                    <Eye className="w-5 h-5" />
                  ) : (
                    <CheckCircle className="w-5 h-5" />
                  )}
                  {executeResult.dry_run ? 'Dry Run Complete' : 'Reset Complete'}
                </CardTitle>
                <CardDescription className="text-slate-400">
                  {executeResult.message}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <div className="text-xs text-slate-400 font-semibold">Epoch</div>
                    <div className="text-lg font-bold text-white">
                      {executeResult.old_epoch} → {executeResult.new_epoch}
                    </div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <div className="text-xs text-slate-400 font-semibold">Docs Deleted</div>
                    <div className="text-lg font-bold text-red-400">
                      {executeResult.total_documents_deleted}
                    </div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <div className="text-xs text-slate-400 font-semibold">Users Reset</div>
                    <div className="text-lg font-bold text-amber-400">
                      {executeResult.users_reset}
                    </div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <div className="text-xs text-slate-400 font-semibold">Audit ID</div>
                    <div className="text-xs font-mono text-slate-300 truncate">
                      {executeResult.audit_log_id?.split('_')[1]?.slice(0, 8)}
                    </div>
                  </div>
                </div>
                
                {!executeResult.dry_run && (
                  <div className="mt-4 p-3 bg-green-900/30 rounded-lg border border-green-700/50">
                    <div className="text-green-300 font-bold">Next Steps:</div>
                    <ul className="text-green-200/80 text-sm mt-1 list-disc list-inside">
                      <li>Admin dashboards should now show 0 for all metrics</li>
                      <li>Readers will see "first login" experience (card capture required)</li>
                      <li>New economic activity will be recorded under epoch {executeResult.new_epoch}</li>
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

        </main>
      </div>

      {/* Final Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="bg-slate-800 border-red-700">
          <DialogHeader>
            <DialogTitle className="text-red-300 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Final Confirmation
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This action cannot be undone. All economic data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-red-900/30 rounded-lg p-4 border border-red-700/50">
              <div className="text-white font-bold mb-2">You are about to:</div>
              <ul className="text-red-200 text-sm space-y-1">
                <li>• Delete {preview?.total_documents_to_delete || 0} financial records</li>
                <li>• Reset {preview?.users_to_reset || 0} user economic states</li>
                <li>• Clear {preview?.billing_profiles_to_clear || 0} billing profiles</li>
                <li>• Advance to epoch {preview?.proposed_epoch || '?'}</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
              className="border-slate-600"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => executeReset(false)}
              disabled={executing}
              className="bg-red-600 hover:bg-red-700 font-bold"
            >
              {executing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Yes, Execute Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UniversalFooter />
    </div>
  );
};

export default AdminResetPage;
